import React from 'react';
import { Search, User, X } from 'lucide-react';
import { ProjectFilters } from '../../utils/projectUtils';
import { AssigneeOption } from '../../utils/projectAssignees';
import { PRIORITIES, PRIORITY_LABELS } from '../../utils/projectConstants';

interface ProjectFiltersBarProps {
  filters: ProjectFilters;
  onChange: (patch: Partial<ProjectFilters>) => void;
  assigneeOptions: AssigneeOption[];
  tags: string[];
}

const ProjectFiltersBar: React.FC<ProjectFiltersBarProps> = ({
  filters,
  onChange,
  assigneeOptions,
  tags,
}) => {
  const hasActive = filters.search || filters.assignee || filters.priority || filters.tag
    || filters.dateFrom || filters.dateTo || filters.myTasks || filters.hideCompleted;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[180px] max-w-xs">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          type="text"
          value={filters.search}
          onChange={e => onChange({ search: e.target.value })}
          placeholder="Buscar tareas..."
          className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-1 focus:ring-indigo-400"
        />
      </div>

      <select
        value={filters.assignee}
        onChange={e => onChange({ assignee: e.target.value })}
        className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 outline-none"
      >
        <option value="">Responsable</option>
        {assigneeOptions.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
      </select>

      <select
        value={filters.priority}
        onChange={e => onChange({ priority: e.target.value })}
        className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 outline-none"
      >
        <option value="">Prioridad</option>
        {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
      </select>

      {tags.length > 0 && (
        <select
          value={filters.tag}
          onChange={e => onChange({ tag: e.target.value })}
          className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 outline-none"
        >
          <option value="">Tag</option>
          {tags.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      )}

      <input
        type="date"
        value={filters.dateFrom}
        onChange={e => onChange({ dateFrom: e.target.value })}
        className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 outline-none"
        title="Desde"
      />
      <input
        type="date"
        value={filters.dateTo}
        onChange={e => onChange({ dateTo: e.target.value })}
        className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 outline-none"
        title="Hasta"
      />

      <button
        type="button"
        onClick={() => onChange({ myTasks: !filters.myTasks })}
        className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
          filters.myTasks
            ? 'bg-indigo-600 text-white border-indigo-600'
            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        <User className="w-3 h-3" />
        Mis tareas
      </button>

      <button
        type="button"
        onClick={() => onChange({ hideCompleted: !filters.hideCompleted })}
        className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
          filters.hideCompleted
            ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900 border-transparent'
            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        Ocultar completadas
      </button>

      {hasActive && (
        <button
          type="button"
          onClick={() => onChange({
            search: '', assignee: '', priority: '', tag: '',
            dateFrom: '', dateTo: '', myTasks: false, hideCompleted: false,
          })}
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <X className="w-3 h-3" /> Limpiar
        </button>
      )}
    </div>
  );
};

export default ProjectFiltersBar;
